import { createContext, useContext, useState } from 'react';

import { logError } from '@zg-rentals/log-error';

import { useCheckDiscriminatoryContent } from '../hooks/queries/useCheckDiscriminatoryContent';
import { useGetPropertyFields } from '../hooks/queries/useGetPropertyFields';
import { useMultiEditMutation } from '../hooks/useMultiEditMutation';
import { markAiDraftStarted } from '../utils/aiDraftCacheUtils';
import { buildAiDraftSaveData } from '../utils/amenitiesUtils';

import type { ReactNode } from 'react';
import type {
  GetUserPropertyDetailsAmenitiesGroupWebDto,
  ListingAlias,
} from '@zg-rentals/ts-rental-manager-api-client';

type AiDraftGeneration = {
  /** Resolves false when the notes fail the pledge check. Cached per text, so Next stays instant. */
  checkNotesForPledge: (notes: string) => Promise<boolean>;
  generateAiDraft: (notes: string) => void;
  /** True from the moment the draft is generated until it is saved onto the listing. */
  isSavingAiDraft: boolean;
};

type Props = {
  listingAlias: string;
  groups: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>;
  children: ReactNode;
};

const AiDraftGenerationContext = createContext<AiDraftGeneration>({
  checkNotesForPledge: () => Promise.resolve(true),
  generateAiDraft: () => {},
  isSavingAiDraft: false,
});

export const useAiDraftGeneration = () => useContext(AiDraftGenerationContext);

/**
 * Owns the generate call for the whole wizard rather than the step that starts it. The AI step
 * unmounts the moment the landlord is moved on to the next step, and a mutation callback dies with
 * the component that registered it, so a callback living there wrote the draft only sometimes.
 */
export const AiDraftGenerationProvider = ({ listingAlias, groups, children }: Props) => {
  const checkDiscriminatoryContent = useCheckDiscriminatoryContent();
  const { mutate: getPropertyFields } = useGetPropertyFields();
  const { mutateAsync: saveDraftToListing } = useMultiEditMutation({
    meta: { context: { api: 'multi', details: 'AiDraftGeneration.saveAiDraft' } },
  });

  // Held from the request until the save settles, so the drafting screen covers the write as well as
  // the generate. Reading the flag off the save mutation alone would leave a gap between the two
  // calls where nothing is pending and the step would flash an empty "Your draft is ready".
  const [isSavingAiDraft, setIsSavingAiDraft] = useState(false);

  // Notes that fail the pledge would only fail again inside the model, so they are stopped here
  // rather than after a generate the landlord cannot come back from.
  const checkNotesForPledge = async (notes: string) => {
    try {
      const { containsDiscriminatoryContent } = await checkDiscriminatoryContent({
        listingAlias: listingAlias as ListingAlias,
        text: notes,
      });

      return !containsDiscriminatoryContent;
    } catch (err) {
      // A check that cannot run must not stop the landlord from drafting.
      logError({
        error: err instanceof Error ? err : new Error('Discriminatory content check failed'),
        context: { details: 'AiDraftGeneration.checkNotesForPledge' },
      });
      return true;
    }
  };

  const generateAiDraft = (notes: string) => {
    markAiDraftStarted(listingAlias, Date.now(), notes);
    setIsSavingAiDraft(true);

    getPropertyFields(
      { postData: { listingAlias, text: notes } },
      {
        // Nothing awaits the save, and the step that started it has already moved on.
        onSuccess: (response) =>
          void saveDraftToListing({
            postData: buildAiDraftSaveData({
              listingAlias,
              groups,
              aiAmenityFields: response.amenityFields,
              description: response.description?.value,
            }),
          })
            .catch(() => undefined)
            .finally(() => setIsSavingAiDraft(false)),
        onError: () => setIsSavingAiDraft(false),
      },
    );
  };

  return (
    <AiDraftGenerationContext.Provider value={{ checkNotesForPledge, generateAiDraft, isSavingAiDraft }}>
      {children}
    </AiDraftGenerationContext.Provider>
  );
};
